/**
 * Products List Page (Admin)
 * Display all products with search, filter, and pagination
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { productsAdminApi, type ProductListParams } from '../../../api/admin/products.api';
import DataTable, { type Column } from '../../../components/ui/DataTable';
import Pagination from '../../../components/ui/Pagination';
import Badge from '../../../components/ui/Badge';

interface Product {
  id: number;
  name: string;
  description?: string;
  productType: string;
  status: 'active' | 'inactive' | 'hidden';
  paymentType: string;
  stockControl: number;
  stockQuantity?: number;
  order: number;
  createdAt: string;
}

export default function ProductsListPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(25);

  // Filter state
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('order');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Load products
  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: ProductListParams = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy: sortBy as any,
        sortOrder,
      };

      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter as any;
      if (typeFilter) params.productType = typeFilter as any;

      const response = await productsAdminApi.list(params);

      setProducts(response.data);
      setTotalPages(response.pagination.totalPages);
      setTotalItems(response.pagination.total);
    } catch (err: any) {
      setError(err.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, [currentPage, sortBy, sortOrder, statusFilter, typeFilter]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage === 1) {
        loadProducts();
      } else {
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const handleSort = (key: string, order: 'asc' | 'desc') => {
    setSortBy(key);
    setSortOrder(order);
  };

  const handleRowClick = (product: Product) => {
    navigate(`/admin/products/${product.id}`);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'danger' | 'default'> = {
      active: 'success',
      inactive: 'default',
      hidden: 'warning',
    };
    return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
  };

  const getTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      hosting: 'bg-blue-900/50 text-blue-300 border-blue-700',
      domain: 'bg-purple-900/50 text-purple-300 border-purple-700',
      addon: 'bg-green-900/50 text-green-300 border-green-700',
      other: 'bg-slate-800/80 text-slate-300 border-slate-700/50',
    };
    return (
      <span className={`inline-flex items-center rounded-full border font-medium text-sm px-2.5 py-0.5 ${colors[type]}`}>
        {type}
      </span>
    );
  };

  const columns: Column<Product>[] = [
    {
      key: 'order',
      label: 'Order',
      sortable: true,
    },
    {
      key: 'name',
      label: 'Product',
      sortable: true,
      render: (product) => (
        <div>
          <div className="font-medium text-white">{product.name}</div>
          {product.description && (
            <div className="text-xs text-slate-400 truncate max-w-md">
              {product.description.substring(0, 100)}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'productType',
      label: 'Type',
      render: (product) => getTypeBadge(product.productType),
    },
    {
      key: 'paymentType',
      label: 'Payment',
      render: (product) => (
        <span className="capitalize text-slate-300">{product.paymentType}</span>
      ),
    },
    {
      key: 'stock',
      label: 'Stock',
      render: (product) => (
        product.stockControl ? (
          <span className={product.stockQuantity! > 0 ? 'text-green-400' : 'text-red-400'}>
            {product.stockQuantity} units
          </span>
        ) : (
          <span className="text-gray-500">Unlimited</span>
        )
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (product) => getStatusBadge(product.status),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-slate-800/80 border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Products</h1>
              <p className="text-slate-400 mt-1">Manage your products and pricing</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/admin/product-groups')}
                className="px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 rounded-lg font-medium"
              >
                Manage Groups
              </button>
              <button
                onClick={() => navigate('/admin/products/new')}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg font-medium"
              >
                + Add Product
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="mt-6 flex items-center space-x-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full px-4 py-2 bg-gray-900 border border-slate-700/50 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-2 bg-gray-900 border border-slate-700/50 rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Types</option>
              <option value="hosting">Hosting</option>
              <option value="domain">Domain</option>
              <option value="addon">Addon</option>
              <option value="other">Other</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 bg-gray-900 border border-slate-700/50 rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="hidden">Hidden</option>
            </select>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {error && (
          <div className="mb-4 p-4 bg-red-900/50 border border-red-700 rounded-lg text-red-300">
            {error}
          </div>
        )}

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
          <DataTable
            data={products}
            columns={columns}
            keyField="id"
            loading={loading}
            emptyMessage="No products found"
            onSort={handleSort}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onRowClick={handleRowClick}
          />

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
          />
        </div>
      </div>
    </div>
  );
}
